import * as React from "react";

import type { FilterDefinition } from "@/shared/components/filters/FilterDefinition";
import type { Filter } from "@/shared/components/filters/FilterSchema";
import { FilterAltIcon } from "@/shared/foundation/icons/mui";

import { FieldPicker } from "./FieldPicker";
import { FilterForm } from "./FilterForm";
import styles from "./PropertyFilterMenu.module.css";

export interface PropertyFilterMenuProps {
  filterDefinitions: FilterDefinition[];
  /** Called when the user clicks Add filter. Emits the raw `Filter`; the
   *  caller wraps it into `FilterMenuOutput` if plumbing through
   *  `view.commit`. */
  onCommit: (filter: Filter) => void;
  /** Called when the user clicks Cancel. */
  onCancel: () => void;
  /**
   * Field to open with already chosen, skipping the "pick a field" state.
   *
   * Seeds initial state only. The pane unmounts with its popover, so each
   * open reads this afresh; it is not a controlled value and changing it on a
   * mounted pane does nothing.
   */
  initialField?: FilterDefinition | null;
}

/**
 * The "Properties" pane of the filter menu — layout shell + field-selection
 * coordinator. Mounts when the popover opens and unmounts when it closes
 * (MUI Popover's default behavior with `keepMounted={false}`), so every
 * open starts with `selectedField` back at whatever `initialField` says —
 * `null` for an ordinary open, a field when the caller opened the menu on
 * one (the View picker's empty-state chips do).
 *
 * All form state (operator, values, validity, submit) lives one level down
 * in `FilterForm`, which only mounts after a field has been picked. The
 * `key={selectedField.field}` remounts `FilterForm` cleanly when the user
 * switches fields — fresh useForm with fresh defaults, no imperative reset
 * choreography.
 *
 * Exported via the `Filters` barrel so M5's `StructuredActiveFilters` can
 * mount it directly inside the `+` button popover, bypassing the outer
 * mode-switching `<FilterMenu>` shell.
 */
export function PropertyFilterMenu({
  filterDefinitions,
  onCommit,
  onCancel,
  initialField = null,
}: PropertyFilterMenuProps) {
  const [selectedField, setSelectedField] =
    React.useState<FilterDefinition | null>(initialField);

  return (
    <div className={styles.popoverShell}>
      <div className={styles.popoverBody}>
        <FieldPicker
          filterDefinitions={filterDefinitions}
          onSelect={setSelectedField}
          selectedField={selectedField}
        />
        {selectedField === null ? (
          <FilterFormEmpty />
        ) : (
          <FilterForm
            // Reset form when selectedField changes
            key={selectedField.field}
            selectedField={selectedField}
            onCommit={onCommit}
            onCancel={onCancel}
          />
        )}
      </div>
    </div>
  );
}

function FilterFormEmpty() {
  return (
    <div className={styles.emptyPanel}>
      <div className={styles.empty}>
        <div className={styles.emptyIcon} aria-hidden="true">
          <FilterAltIcon fontSize="medium" />
        </div>
        <p className={styles.emptyTitle}>Pick a field to filter on</p>
        <p className={styles.emptyBody}>
          Choose a field, set an operator, and enter a value.
        </p>
      </div>
    </div>
  );
}
